import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { discoveryAPI, aiAPI, preferencesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Heart, X, Star, MessageCircle, MapPin, Activity, ArrowLeft, Sparkles, Home, User, Settings, Users, Flame, Filter, Check, BadgeCheck, Sliders } from 'lucide-react';
import { ScoreLogo } from '../components/Logo';
import { toast } from 'sonner';

export const Discovery = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(null);
  const [showMatch, setShowMatch] = useState(false);
  const [matchedProfile, setMatchedProfile] = useState(null);
  const [icebreakers, setIcebreakers] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSports, setSelectedSports] = useState([]);
  
  // Advanced filters
  const [advancedFilters, setAdvancedFilters] = useState({
    verifiedOnly: false,
    ethnicity: '',
    religion: '',
    smoking: '',
    drinking: '',
    heightMin: '',
    heightMax: '',
    education: ''
  });

  const SPORTS_LIST = [
    "Pickleball", "Tennis", "Running", "Cycling", "Golf", "Hiking", "Yoga",
    "CrossFit / Functional Fitness", "Soccer", "Basketball", "Softball", "Baseball",
    "Football/Flag Football", "Swimming", "Volleyball", "Skiing/Snowboarding",
    "Bowling", "Rock Climbing", "Martial Arts/MMA", "Ice Hockey", "Lacrosse",
    "Rugby", "Padel", "Racquetball", "Table Tennis (Ping Pong)", "Badminton",
    "Squash", "Gymnastics", "Motorsport", "Cricket", "Field Hockey",
    "Cheerleading", "Equestrian"
  ];

  const ETHNICITY_OPTIONS = ["Asian", "Black", "Hispanic/Latino", "White", "Middle Eastern", "Native American", "Pacific Islander", "Mixed", "Other"];
  const RELIGION_OPTIONS = ["Agnostic", "Atheist", "Buddhist", "Catholic", "Christian", "Hindu", "Jewish", "Muslim", "Spiritual", "Other"];
  const SMOKING_OPTIONS = ["Never", "Socially", "Regularly"];
  const DRINKING_OPTIONS = ["Never", "Socially", "Regularly"];
  const EDUCATION_OPTIONS = ["High School", "Some College", "Bachelor's", "Master's", "PhD", "Trade School"];
  const HEIGHT_OPTIONS = ["4'10\"", "5'0\"", "5'2\"", "5'4\"", "5'6\"", "5'8\"", "5'10\"", "6'0\"", "6'2\"", "6'4\"", "6'6\""];

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedSports, advancedFilters, profiles]);

  useEffect(() => {
    // Load AI suggestion when profile changes
    if (filteredProfiles[currentIndex]) {
      loadAiSuggestion(filteredProfiles[currentIndex]);
    }
  }, [currentIndex, filteredProfiles]);

  const applyFilters = () => {
    let filtered = [...profiles];
    
    // Sports filter
    if (selectedSports.length > 0) {
      filtered = filtered.filter(profile => {
        const profileSports = profile.sports?.map(s => s.sport) || [];
        return selectedSports.some(sport => profileSports.includes(sport));
      });
    }
    
    // Verified only filter
    if (advancedFilters.verifiedOnly) {
      filtered = filtered.filter(p => p.selfie_verified);
    }
    
    // Ethnicity filter
    if (advancedFilters.ethnicity) {
      filtered = filtered.filter(p => p.ethnicity === advancedFilters.ethnicity);
    }
    
    // Religion filter
    if (advancedFilters.religion) {
      filtered = filtered.filter(p => p.religious_beliefs === advancedFilters.religion);
    }
    
    // Smoking filter
    if (advancedFilters.smoking) {
      filtered = filtered.filter(p => p.smoking === advancedFilters.smoking);
    }
    
    // Drinking filter
    if (advancedFilters.drinking) {
      filtered = filtered.filter(p => p.drinking === advancedFilters.drinking);
    }
    
    // Education filter
    if (advancedFilters.education) {
      filtered = filtered.filter(p => p.education_level === advancedFilters.education);
    }
    
    // Height filters (simple string comparison for now)
    if (advancedFilters.heightMin || advancedFilters.heightMax) {
      filtered = filtered.filter(p => {
        if (!p.height) return false;
        const heightInches = parseHeightToInches(p.height);
        if (!heightInches) return false;
        
        if (advancedFilters.heightMin) {
          const minInches = parseHeightToInches(advancedFilters.heightMin);
          if (minInches && heightInches < minInches) return false;
        }
        if (advancedFilters.heightMax) {
          const maxInches = parseHeightToInches(advancedFilters.heightMax);
          if (maxInches && heightInches > maxInches) return false;
        }
        return true;
      });
    }
    
    setFilteredProfiles(filtered);
    setCurrentIndex(0);
  };

  const parseHeightToInches = (heightStr) => {
    try {
      const cleaned = heightStr.replace(/"/g, '').replace(/'/g, "'").trim();
      if (cleaned.includes("'")) {
        const parts = cleaned.split("'");
        const feet = parseInt(parts[0].trim());
        const inches = parts[1] ? parseInt(parts[1].trim()) : 0;
        return feet * 12 + inches;
      }
      return null;
    } catch {
      return null;
    }
  };

  const getActiveFilterCount = () => {
    let count = selectedSports.length;
    if (advancedFilters.verifiedOnly) count++;
    if (advancedFilters.ethnicity) count++;
    if (advancedFilters.religion) count++;
    if (advancedFilters.smoking) count++;
    if (advancedFilters.drinking) count++;
    if (advancedFilters.education) count++;
    if (advancedFilters.heightMin) count++;
    if (advancedFilters.heightMax) count++;
    return count;
  };

  const clearAllFilters = () => {
    setSelectedSports([]);
    setAdvancedFilters({
      verifiedOnly: false,
      ethnicity: '',
      religion: '',
      smoking: '',
      drinking: '',
      heightMin: '',
      heightMax: '',
      education: ''
    });
  };

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const response = await discoveryAPI.getProfiles();
      setProfiles(response.data);
      setFilteredProfiles(response.data);
    } catch (error) {
      console.error('Failed to load profiles:', error);
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const toggleSportFilter = (sport) => {
    setSelectedSports(prev => 
      prev.includes(sport) 
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const clearFilters = () => {
    setSelectedSports([]);
  };

  const loadAiSuggestion = async (profile) => {
    try {
      const response = await aiAPI.getSuggestion(profile.user_id);
      setAiSuggestion(response.data.suggestion);
    } catch (error) {
      // Silently fail - AI suggestion is optional
      setAiSuggestion(null);
    }
  };

  const handleSwipe = async (action) => {
    if (currentIndex >= filteredProfiles.length) return;
    
    const currentProfile = filteredProfiles[currentIndex];
    setSwiping(action);

    try {
      const response = await discoveryAPI.swipe({
        target_user_id: currentProfile.user_id,
        action: action
      });

      if (response.data.matched) {
        setMatchedProfile(currentProfile);
        // Load icebreakers for the match
        try {
          const icebreakerRes = await aiAPI.getIcebreakers(currentProfile.user_id);
          setIcebreakers(icebreakerRes.data.icebreakers || []);
        } catch (e) {
          console.error('Failed to load icebreakers:', e);
        }
        setShowMatch(true);
      }

      setTimeout(() => {
        setSwiping(null);
        setCurrentIndex(currentIndex + 1);
      }, 300);
    } catch (error) {
      console.error('Swipe failed:', error);
      toast.error('Action failed');
      setSwiping(null);
    }
  };

  const currentProfile = filteredProfiles[currentIndex];

  const levelColors = {
    beginner: 'bg-green-500',
    intermediate: 'bg-blue-500',
    advanced: 'bg-yellow-500',
    elite: 'bg-orange-500',
    college_pro: 'bg-red-500'
  };

  const levelLabels = {
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    elite: 'Elite',
    college_pro: 'College/Pro'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="font-body text-gray-500">Finding matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-lg mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ScoreLogo size={32} />
            <span className="font-display font-bold text-xl tracking-tight text-gray-900">SCORE</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Filters Button */}
            <Sheet open={showFilters} onOpenChange={setShowFilters}>
              <SheetTrigger asChild>
                <Button 
                  data-testid="filter-btn"
                  variant="ghost" 
                  size="icon"
                  className={`relative hover:bg-gray-100 transition-colors ${getActiveFilterCount() > 0 ? 'text-primary' : ''}`}
                >
                  <Sliders className="w-5 h-5" />
                  {getActiveFilterCount() > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                      {getActiveFilterCount()}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[340px] sm:w-[420px] p-0">
                <SheetHeader className="p-6 pb-0">
                  <SheetTitle className="font-display text-xl uppercase text-gray-900">Filters</SheetTitle>
                </SheetHeader>
                
                <ScrollArea className="h-[calc(100vh-180px)] px-6">
                  <div className="space-y-6 py-4">
                    {/* Verified Only Toggle */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="flex items-center gap-3">
                        <BadgeCheck className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="font-body font-medium text-gray-900">Verified Only</p>
                          <p className="font-body text-xs text-gray-500">Show only verified profiles</p>
                        </div>
                      </div>
                      <Switch 
                        checked={advancedFilters.verifiedOnly}
                        onCheckedChange={(v) => setAdvancedFilters(prev => ({...prev, verifiedOnly: v}))}
                      />
                    </div>
                    
                    {/* Height Range */}
                    <div className="space-y-3">
                      <Label className="font-body font-medium text-gray-700">Height Range</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <Select 
                          value={advancedFilters.heightMin || 'any'} 
                          onValueChange={(v) => setAdvancedFilters(prev => ({...prev, heightMin: v === 'any' ? '' : v}))}
                        >
                          <SelectTrigger className="bg-white border-gray-200">
                            <SelectValue placeholder="Min height" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {HEIGHT_OPTIONS.map(h => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={advancedFilters.heightMax || 'any'} 
                          onValueChange={(v) => setAdvancedFilters(prev => ({...prev, heightMax: v === 'any' ? '' : v}))}
                        >
                          <SelectTrigger className="bg-white border-gray-200">
                            <SelectValue placeholder="Max height" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {HEIGHT_OPTIONS.map(h => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Ethnicity */}
                    <div className="space-y-3">
                      <Label className="font-body font-medium text-gray-700">Ethnicity</Label>
                      <Select 
                        value={advancedFilters.ethnicity || 'any'} 
                        onValueChange={(v) => setAdvancedFilters(prev => ({...prev, ethnicity: v === 'any' ? '' : v}))}
                      >
                        <SelectTrigger className="bg-white border-gray-200">
                          <SelectValue placeholder="Any ethnicity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          {ETHNICITY_OPTIONS.map(e => (
                            <SelectItem key={e} value={e}>{e}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Religion */}
                    <div className="space-y-3">
                      <Label className="font-body font-medium text-gray-700">Religion</Label>
                      <Select 
                        value={advancedFilters.religion || 'any'} 
                        onValueChange={(v) => setAdvancedFilters(prev => ({...prev, religion: v === 'any' ? '' : v}))}
                      >
                        <SelectTrigger className="bg-white border-gray-200">
                          <SelectValue placeholder="Any religion" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          {RELIGION_OPTIONS.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Lifestyle */}
                    <div className="space-y-3">
                      <Label className="font-body font-medium text-gray-700">Lifestyle</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <Select 
                          value={advancedFilters.smoking || 'any'} 
                          onValueChange={(v) => setAdvancedFilters(prev => ({...prev, smoking: v === 'any' ? '' : v}))}
                        >
                          <SelectTrigger className="bg-white border-gray-200">
                            <SelectValue placeholder="Smoking" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {SMOKING_OPTIONS.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={advancedFilters.drinking || 'any'} 
                          onValueChange={(v) => setAdvancedFilters(prev => ({...prev, drinking: v === 'any' ? '' : v}))}
                        >
                          <SelectTrigger className="bg-white border-gray-200">
                            <SelectValue placeholder="Drinking" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {DRINKING_OPTIONS.map(d => (
                              <SelectItem key={d} value={d}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Education */}
                    <div className="space-y-3">
                      <Label className="font-body font-medium text-gray-700">Education</Label>
                      <Select 
                        value={advancedFilters.education || 'any'} 
                        onValueChange={(v) => setAdvancedFilters(prev => ({...prev, education: v === 'any' ? '' : v}))}
                      >
                        <SelectTrigger className="bg-white border-gray-200">
                          <SelectValue placeholder="Any education" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          {EDUCATION_OPTIONS.map(e => (
                            <SelectItem key={e} value={e}>{e}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Sports Section */}
                    <div className="space-y-3">
                      <Label className="font-body font-medium text-gray-700">Sports ({selectedSports.length} selected)</Label>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                        {SPORTS_LIST.map(sport => (
                          <div 
                            key={sport}
                            className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                              selectedSports.includes(sport) 
                                ? 'bg-primary/10 border border-primary' 
                                : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                            }`}
                            onClick={() => toggleSportFilter(sport)}
                          >
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                              selectedSports.includes(sport) 
                                ? 'bg-primary border-primary' 
                                : 'border-gray-300'
                            }`}>
                              {selectedSports.includes(sport) && (
                                <Check className="w-2.5 h-2.5 text-white" />
                              )}
                            </div>
                            <span className="font-body text-sm text-gray-700">{sport}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollArea>
                
                <div className="p-6 border-t border-gray-100 bg-white">
                  <div className="flex gap-3">
                    {getActiveFilterCount() > 0 && (
                      <Button 
                        variant="outline"
                        className="flex-1 border-gray-200"
                        onClick={clearAllFilters}
                      >
                        Clear All
                      </Button>
                    )}
                    <Button 
                      className={`${getActiveFilterCount() > 0 ? 'flex-1' : 'w-full'} bg-primary hover:bg-red-600`}
                      onClick={() => setShowFilters(false)}
                    >
                      Show {filteredProfiles.length} Results
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <Button 
              data-testid="find-partner-btn"
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/find-partner')}
              className="relative hover:bg-gray-100 transition-colors"
            >
              <Users className="w-5 h-5 text-primary" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-28 px-6">
        <div className="max-w-lg mx-auto">
          {currentIndex >= filteredProfiles.length ? (
            <div className="h-[70vh] flex flex-col items-center justify-center text-center px-8">
              <Activity className="w-16 h-16 text-gray-300 mb-6" />
              <h2 className="font-display text-3xl uppercase mb-4 tracking-tight text-gray-900">
                {getActiveFilterCount() > 0 ? 'No Matching Profiles' : 'No More Profiles'}
              </h2>
              <p className="font-body text-gray-500 mb-8 leading-relaxed">
                {getActiveFilterCount() > 0 
                  ? 'Try adjusting your filters to see more profiles'
                  : 'Check back later for new athletes in your area'}
              </p>
              <div className="flex gap-3">
                {getActiveFilterCount() > 0 && (
                  <Button 
                    variant="outline"
                    onClick={clearAllFilters}
                    className="px-6 py-3 rounded-full border-gray-200"
                  >
                    Clear Filters
                  </Button>
                )}
                <Button 
                  onClick={loadProfiles}
                  className="px-8 py-3 rounded-full shadow-lg"
                >
                  Refresh
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative h-[70vh]">
              <AnimatePresence>
                {currentProfile && (
                  <motion.div
                    key={currentProfile.user_id}
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ 
                      scale: 1, 
                      opacity: 1,
                      x: swiping === 'pass' ? -300 : swiping === 'like' ? 300 : swiping === 'super_like' ? 0 : 0,
                      y: swiping === 'super_like' ? -100 : 0,
                      rotate: swiping === 'pass' ? -15 : swiping === 'like' ? 15 : 0
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0"
                  >
                    <Card 
                      data-testid="profile-card"
                      className="h-full w-full rounded-3xl overflow-hidden border border-gray-200 shadow-2xl bg-white"
                    >
                      {/* Profile Image */}
                      <div className="relative h-full">
                        <img
                          src={currentProfile.photos?.[0] || currentProfile.picture || 'https://images.unsplash.com/photo-1633354129320-163fb10dd288?w=400'}
                          alt={currentProfile.name}
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                        {/* Swipe Indicators */}
                        {swiping === 'like' && (
                          <div className="absolute top-8 left-8 px-6 py-3 rounded-full border-4 border-green-500 rotate-[-20deg]">
                            <span className="font-display font-bold text-2xl text-green-500">LIKE</span>
                          </div>
                        )}
                        {swiping === 'pass' && (
                          <div className="absolute top-8 right-8 px-6 py-3 rounded-full border-4 border-red-500 rotate-[20deg]">
                            <span className="font-display font-bold text-2xl text-red-500">NOPE</span>
                          </div>
                        )}
                        {swiping === 'super_like' && (
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-6 py-3 rounded-full border-4 border-blue-500">
                            <span className="font-display font-bold text-2xl text-blue-500">SUPER LIKE</span>
                          </div>
                        )}

                        {/* Profile Info */}
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="flex items-end justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h2 className="font-display font-bold text-3xl text-white">
                                  {currentProfile.name}, {currentProfile.age}
                                </h2>
                                {/* Verification Badge */}
                                {currentProfile.selfie_verified && (
                                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500 text-white">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-xs font-medium">Verified</span>
                                  </div>
                                )}
                              </div>
                              {currentProfile.location && (
                                <div className="flex items-center gap-1 text-white/80 mt-1">
                                  <MapPin className="w-4 h-4" />
                                  <span className="font-body text-sm">{currentProfile.location}</span>
                                </div>
                              )}
                              {/* Sports */}
                              <div className="flex flex-wrap gap-2 mt-3">
                                {currentProfile.sports?.map((sport, i) => (
                                  <div 
                                    key={i}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm"
                                  >
                                    <span className={`w-2 h-2 rounded-full ${levelColors[sport.level]}`} />
                                    <span className="font-body text-sm text-white">{sport.sport}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <Button
                              data-testid="view-profile-btn"
                              size="icon"
                              variant="ghost"
                              className="rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20"
                              onClick={() => navigate(`/profile/${currentProfile.user_id}`)}
                            >
                              <ArrowLeft className="w-5 h-5 rotate-180" />
                            </Button>
                          </div>

                          {/* Prompts Preview */}
                          {currentProfile.prompts?.length > 0 && (
                            <div className="mt-4 p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                              <p className="font-body text-sm text-white/70 mb-1">
                                {currentProfile.prompts[0].prompt}
                              </p>
                              <p className="font-body text-white">
                                {currentProfile.prompts[0].answer}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Action Buttons */}
          {currentIndex < filteredProfiles.length && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <Button
                data-testid="pass-btn"
                size="lg"
                variant="outline"
                className="w-16 h-16 rounded-full border-2 border-gray-200 hover:border-red-500 hover:text-red-500 bg-white shadow-lg"
                onClick={() => handleSwipe('pass')}
              >
                <X className="w-8 h-8" />
              </Button>
              <Button
                data-testid="super-like-btn"
                size="lg"
                className="w-14 h-14 rounded-full bg-blue-500 hover:bg-blue-600 shadow-lg"
                onClick={() => handleSwipe('super_like')}
              >
                <Star className="w-6 h-6 fill-white" />
              </Button>
              <Button
                data-testid="like-btn"
                size="lg"
                className="w-16 h-16 rounded-full bg-primary hover:bg-red-600 shadow-lg"
                onClick={() => handleSwipe('like')}
              >
                <Heart className="w-8 h-8" />
              </Button>
            </div>
          )}

          {/* AI Suggestion Card */}
          {aiSuggestion && currentProfile && (
            <div className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span className="font-body text-sm font-medium text-purple-700">AI Match Insight</span>
              </div>
              <p className="font-body text-sm text-gray-700">{aiSuggestion}</p>
            </div>
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-2 flex items-center justify-around">
          <Button 
            data-testid="nav-discover"
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-primary"
          >
            <Activity className="w-5 h-5" />
            <span className="text-xs font-body">Discover</span>
          </Button>
          <Button 
            data-testid="nav-matches"
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/matches')}
          >
            <Heart className="w-5 h-5" />
            <span className="text-xs font-body">Matches</span>
          </Button>
          <Button 
            data-testid="nav-messages"
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/messages')}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs font-body">Messages</span>
          </Button>
          <Button 
            data-testid="nav-profile"
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/profile')}
          >
            <User className="w-5 h-5" />
            <span className="text-xs font-body">Profile</span>
          </Button>
          <Button 
            data-testid="nav-settings"
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/settings')}
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs font-body">Settings</span>
          </Button>
        </div>
      </nav>

      {/* Match Modal */}
      {showMatch && matchedProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center p-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary">
                  <img 
                    src={user?.picture || 'https://via.placeholder.com/150'} 
                    alt="You"
                    className="w-full h-full object-cover"
                  />
                </div>
                <Heart className="w-12 h-12 text-primary fill-primary match-pulse" />
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary">
                  <img 
                    src={matchedProfile.photos?.[0] || matchedProfile.picture || 'https://via.placeholder.com/150'} 
                    alt={matchedProfile.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            <h2 className="font-display font-black text-4xl text-primary uppercase mb-2">
              IT'S A MATCH!
            </h2>
            <p className="font-body text-muted-foreground mb-6">
              You and {matchedProfile.name} liked each other
            </p>

            {/* AI Icebreakers */}
            {icebreakers.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-body text-sm text-muted-foreground">AI Conversation Starters</span>
                </div>
                <div className="space-y-2">
                  {icebreakers.map((icebreaker, i) => (
                    <button
                      key={i}
                      className="w-full p-3 rounded-xl bg-secondary text-left hover:bg-secondary/80 transition-colors"
                      onClick={() => {
                        navigate('/messages', { state: { matchId: matchedProfile.match_id, message: icebreaker } });
                      }}
                    >
                      <p className="font-body text-sm">{icebreaker}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <Button
                data-testid="match-message-btn"
                className="bg-primary hover:bg-primary/90"
                onClick={() => navigate('/messages')}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Send Message
              </Button>
              <Button
                data-testid="match-continue-btn"
                variant="outline"
                onClick={() => setShowMatch(false)}
              >
                Keep Swiping
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
